/**
 * Kill confirmed effect and kill-streak counter.
 *
 * On kill: brief white screen-edge flash (0.1 s fade).
 * Kill streak: shows "2K", "3K", "4K", "ACE" text with progressively larger
 * font.  Streak resets on round end or death.
 */

const FLASH_DURATION = 0.1; // seconds
const STREAK_DISPLAY_TIME = 1.5; // seconds before fade starts
const STREAK_FADE_TIME = 0.5; // seconds of fade-out
const STREAK_TOTAL = STREAK_DISPLAY_TIME + STREAK_FADE_TIME; // 2.0 s

const STREAK_LABELS: Record<number, string> = {
  2: '2K',
  3: '3K',
  4: '4K',
  5: 'ACE',
};

const BASE_FONT_SIZE = 28; // px for "2K"
const FONT_SIZE_STEP = 8; // each tier adds this much

export class KillEffect {
  private flashEl: HTMLElement;
  private streakEl: HTMLElement;
  private container: HTMLElement;

  private _streak = 0;
  private _flashAge = 0;
  private _flashActive = false;
  private _streakLabelActive = false;
  private _streakAge = 0;
  private _currentLabel: string | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.style.cssText = [
      'position: fixed',
      'top: 0',
      'left: 0',
      'width: 100%',
      'height: 100%',
      'pointer-events: none',
      'z-index: 31',
    ].join('; ');

    // Screen-edge flash overlay
    this.flashEl = document.createElement('div');
    this.flashEl.style.cssText = [
      'position: absolute',
      'top: 0',
      'left: 0',
      'width: 100%',
      'height: 100%',
      'pointer-events: none',
      'opacity: 0',
      // Vignette-style white flash on edges
      'background: radial-gradient(circle at center, transparent 50%, rgba(255,255,255,0.35) 100%)',
    ].join('; ');

    // Streak label
    this.streakEl = document.createElement('div');
    this.streakEl.style.cssText = [
      'position: absolute',
      'top: 25%',
      'left: 50%',
      'transform: translateX(-50%)',
      'font-family: "Courier New", monospace',
      'font-weight: bold',
      'color: #ffdd00',
      'text-shadow: 0 0 12px rgba(255,221,0,0.7), 2px 2px 4px #000',
      'letter-spacing: 4px',
      'text-transform: uppercase',
      'opacity: 0',
      'pointer-events: none',
    ].join('; ');

    this.container.appendChild(this.flashEl);
    this.container.appendChild(this.streakEl);
    document.body.appendChild(this.container);
  }

  // ── Public API ──

  get streak(): number {
    return this._streak;
  }

  get isFlashActive(): boolean {
    return this._flashActive;
  }

  get isStreakLabelActive(): boolean {
    return this._streakLabelActive;
  }

  get currentStreakLabel(): string | null {
    return this._currentLabel;
  }

  /**
   * Call when the local player confirms a kill.
   *
   * @param _isHeadshot  Whether the kill was a headshot (reserved for future per-kill styling).
   */
  showKill(_isHeadshot: boolean): void {
    // Increment streak
    this._streak++;

    // Trigger flash
    this._flashActive = true;
    this._flashAge = 0;
    this.flashEl.style.opacity = '1';

    // Update streak label
    this.updateStreakLabel();
  }

  /** Reset the kill streak (call on round end or player death). */
  resetStreak(): void {
    this._streak = 0;
    this._currentLabel = null;
    this._streakLabelActive = false;
    this._streakAge = 0;
    this.streakEl.style.opacity = '0';
  }

  /** Advance animations — call once per frame with delta-time in seconds. */
  update(dt: number): void {
    // Flash
    if (this._flashActive) {
      this._flashAge += dt;
      if (this._flashAge >= FLASH_DURATION) {
        this._flashActive = false;
        this.flashEl.style.opacity = '0';
      } else {
        const t = this._flashAge / FLASH_DURATION;
        this.flashEl.style.opacity = String(Math.max(0, 1 - t));
      }
    }

    // Streak label
    if (this._streakLabelActive) {
      this._streakAge += dt;
      if (this._streakAge >= STREAK_TOTAL) {
        this._streakLabelActive = false;
        this.streakEl.style.opacity = '0';
      } else if (this._streakAge > STREAK_DISPLAY_TIME) {
        const fadeT = (this._streakAge - STREAK_DISPLAY_TIME) / STREAK_FADE_TIME;
        this.streakEl.style.opacity = String(Math.max(0, 1 - fadeT));
      }
    }
  }

  /**
   * Get the font size (px) for a given streak count.
   * Progressively larger for higher streaks.
   */
  getStreakFontSize(streakCount: number): number {
    const tier = Math.min(streakCount, 5) - 2; // 2K=0, 3K=1, 4K=2, ACE=3
    return BASE_FONT_SIZE + Math.max(0, tier) * FONT_SIZE_STEP;
  }

  dispose(): void {
    this._streak = 0;
    this._flashActive = false;
    this._streakLabelActive = false;
    this._flashAge = 0;
    this._streakAge = 0;
    this._currentLabel = null;
    this.container.remove();
  }

  // ── Private ──

  private updateStreakLabel(): void {
    // Resolve label: use capped value (5 = ACE for 5+)
    const cappedStreak = Math.min(this._streak, 5);
    const label = STREAK_LABELS[cappedStreak] ?? null;

    this._currentLabel = label;

    if (label) {
      this._streakLabelActive = true;
      this._streakAge = 0;

      const fontSize = this.getStreakFontSize(this._streak);
      this.streakEl.textContent = label;
      this.streakEl.style.fontSize = `${fontSize}px`;
      this.streakEl.style.opacity = '1';
    }
  }
}
